// components/CropperModal.tsx
'use client'
import { useCallback, useEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import RotateLeftIcon from '@mui/icons-material/RotateLeft'
import RotateRightIcon from '@mui/icons-material/RotateRight'
import { EmtButton, EmtDistance, EmtIcon, EmtLoader, EmtModal, useLargeTablet, useMobile } from 'emt-components-v2'
import { AreaPixels, cropToBase64 } from '@/utils/cropImage'
import useCurrentUser from '@/hooks/useCurrentUser'
import { normalizeImage } from '@/utils/normalizeImage'
import { useTranslation } from 'next-i18next'

type Props = {
  open: boolean
  imageSrc: string | null
  onClose: () => void
  onComplete: (base64: string) => void
}

export default function CropperModal({ open, imageSrc, onClose, onComplete }: Props) {
  const { t } = useTranslation('common')
  const [resetKey, setResetKey] = useState(0)
  const { isPetsitterInRegistration, isPetSitter } = useCurrentUser()
  const [normalizedSrc, setNormalizedSrc] = useState<string | null>(null)
  const [imgReady, setImgReady] = useState(false)

  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [aspectRatio, setAspectRatio] = useState(1)

  const [rotation, setRotation] = useState(0) // 0/90/180/270
  const [area, setArea] = useState<AreaPixels | null>(null)
  const [busy, setBusy] = useState(false)
  const [isMobile] = useMobile()
  const isLargeTablet = useLargeTablet()
  const zoneSize = Math.min(600, window.innerWidth - 30)
  const cropSize = (zoneSize * 2) / 3

  const imgRef = useRef<HTMLImageElement | null>(null)

  const handleUse = useCallback(async () => {
    if (!normalizedSrc || !area) return
    setBusy(true)
    try {
      const mappedArea = {
        x: Math.round(area.x),
        y: Math.round(area.y),
        width: Math.round(area.width),
        height: Math.round(area.height),
      }
      const base64 = await cropToBase64(normalizedSrc, mappedArea, rotation, {
        type: 'image/jpeg',
        quality: 0.9,
      })
      onComplete(base64)
    } finally {
      setBusy(false)
    }
  }, [normalizedSrc, area, rotation, onComplete])

  const rotateLeft = () => {
    setRotation((r) => (r - 90 + 360) % 360)
    setCrop((currentCrop) => ({
      x: currentCrop.y,
      y: -currentCrop.x,
    }))
  }

  const rotateRight = () => {
    setRotation((r) => (r + 90) % 360)
    setCrop((currentCrop) => ({
      x: -currentCrop.y,
      y: currentCrop.x,
    }))
  }

  // Reset all states when modal opens
  useEffect(() => {
    if (open) {
      setCrop({ x: 0, y: 0 })
      setZoom(1)
      setRotation(0)
      setArea(null)
      setBusy(false)
      setResetKey((k) => k + 1)
    }
  }, [open, imageSrc])

  useEffect(() => {
    calculateCropArea()
    calculateBoundaries()
  }, [crop, cropSize, zoom, rotation, aspectRatio])

  useEffect(() => {
    setImgReady(false)
    if (!normalizedSrc) return
    const img = new Image()
    img.onload = () => {
      const ratio = img.width / img.height
      setAspectRatio(rotation % 180 === 0 ? ratio : 1 / ratio)
      setImgReady(true)
    }
    img.src = normalizedSrc
  }, [normalizedSrc])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      if (!imageSrc) {
        setNormalizedSrc(null)
        return
      }
      try {
        const norm = await normalizeImage(imageSrc, { mime: 'image/jpeg', quality: 0.92 })
        if (!cancelled) setNormalizedSrc(norm)
      } catch {
        if (!cancelled) setNormalizedSrc(imageSrc) // fallback
      }
    })()
    return () => {
      cancelled = true
    }
  }, [imageSrc])

  useEffect(() => {
    if (imageSrc) setResetKey((k) => k + 1)
  }, [imageSrc])

  const cropperRef = useRef<HTMLDivElement>(null)
  const isDragging = useRef(false)
  const startPos = useRef({ x: 0, y: 0 })

  const calculateCropArea = useCallback(() => {
    const imgElement = imgRef.current
    if (!imgElement) return

    const naturalWidth = imgElement.naturalWidth
    const naturalHeight = imgElement.naturalHeight
    const displayedWidth = imgElement.width
    const displayedHeight = imgElement.height

    if (!naturalWidth || !naturalHeight || !displayedWidth || !displayedHeight) return

    const scaleX = naturalWidth / displayedWidth
    const scaleY = naturalHeight / displayedHeight

    const isRotated = rotation % 180 !== 0
    const effectiveWidth = isRotated ? naturalHeight : naturalWidth
    const effectiveHeight = isRotated ? naturalWidth : naturalHeight

    const areaPixels: AreaPixels = {
      x: effectiveWidth / 2 - (cropSize * scaleX) / (2 * zoom) - (crop.x * scaleX) / zoom,
      y: effectiveHeight / 2 - (cropSize * scaleY) / (2 * zoom) - (crop.y * scaleY) / zoom,
      width: isRotated ? (cropSize * scaleY) / zoom : (cropSize * scaleX) / zoom,
      height: isRotated ? (cropSize * scaleX) / zoom : (cropSize * scaleY) / zoom,
    }

    if (areaPixels.width <= 0 || areaPixels.height <= 0) return
    setArea(areaPixels)
  }, [crop.x, crop.y, cropSize, zoom, rotation])

  useEffect(() => {
    if (!imgReady) return
    requestAnimationFrame(() => calculateCropArea())
  }, [imgReady, calculateCropArea])

  // replace existing function with:
  const calculateBoundaries = useCallback(
    (z = zoom) => {
      if (!cropperRef.current) return { minX: 0, maxX: 0, minY: 0, maxY: 0 }
      const imgElement = cropperRef.current.querySelector('img')
      if (!imgElement) return { minX: 0, maxX: 0, minY: 0, maxY: 0 }

      const naturalWidth = imgElement.naturalWidth
      const naturalHeight = imgElement.naturalHeight
      const displayedWidth = imgElement.width
      const displayedHeight = imgElement.height
      if (!naturalWidth || !naturalHeight || !displayedWidth || !displayedHeight) {
        return { minX: 0, maxX: 0, minY: 0, maxY: 0 }
      }

      const scaleX = naturalWidth / displayedWidth
      const scaleY = naturalHeight / displayedHeight
      const scale = Math.max(scaleX, scaleY)

      const isRotated = rotation % 180 !== 0
      const effectiveWidth = isRotated ? naturalHeight : naturalWidth
      const effectiveHeight = isRotated ? naturalWidth : naturalHeight

      const overflowX = (effectiveWidth * z - cropSize * scale) / 2
      const overflowY = (effectiveHeight * z - cropSize * scale) / 2

      return {
        minX: -overflowX / scale,
        maxX: overflowX / scale,
        minY: -overflowY / scale,
        maxY: overflowY / scale,
      }
    },
    [rotation, cropSize, zoom],
  ) // ok to keep zoom for default arg

  const clampCrop = useCallback(
    (x: number, y: number, z = zoom) => {
      const b = calculateBoundaries(z)
      return {
        x: Math.max(b.minX, Math.min(x, b.maxX)),
        y: Math.max(b.minY, Math.min(y, b.maxY)),
      }
    },
    [calculateBoundaries, zoom],
  )

  // below clampCrop:
  const updateZoom = useCallback(
    (nextZoom: number, anchor?: { x: number; y: number }) => {
      const ax = anchor?.x ?? 0 // default: crop square center
      const ay = anchor?.y ?? 0

      setZoom((prev) => {
        const next = Math.max(1, Math.min(3, nextZoom))
        if (next === prev) return prev

        setCrop((c) => {
          // keep the anchor point stationary while zooming
          const k = next / prev
          const nx = (c.x - ax) * k + ax
          const ny = (c.y - ay) * k + ay
          return clampCrop(nx, ny, next)
        })

        return next
      })
    },
    [clampCrop],
  )

  const handleMouseDown = (e: React.MouseEvent | React.TouchEvent) => {
    isDragging.current = true
    const pos = 'touches' in e ? e.touches[0] : e
    startPos.current = {
      x: pos.clientX - crop.x,
      y: pos.clientY - crop.y,
    }
  }

  const handleMouseMove = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (!isDragging.current) return
      const pos = 'touches' in e ? e.touches[0] : e
      const newX = pos.clientX - startPos.current.x
      const newY = pos.clientY - startPos.current.y

      const boundaries = calculateBoundaries()

      const constrainedX = Math.max(boundaries.minX, Math.min(newX, boundaries.maxX))
      const constrainedY = Math.max(boundaries.minY, Math.min(newY, boundaries.maxY))

      setCrop({ x: constrainedX, y: constrainedY })
      // calculateCropArea()
    },
    [calculateBoundaries, calculateCropArea],
  )

  const handleMouseUp = () => {
    isDragging.current = false
  }

  // Add this effect to adjust crop position when zoom changes
  // useEffect(() => {
  //   const boundaries = calculateBoundaries()
  //   setCrop((currentCrop) => ({
  //     x: Math.max(boundaries.minX, Math.min(currentCrop.x, boundaries.maxX)),
  //     y: Math.max(boundaries.minY, Math.min(currentCrop.y, boundaries.maxY)),
  //   }))
  // }, [zoom])

  // Update the handleWheel function
  const handleWheel = useCallback(
    (e: WheelEvent) => {
      e.preventDefault()
      const box = cropperRef.current?.getBoundingClientRect()
      if (!box) return

      const centerX = box.left + box.width / 2
      const centerY = box.top + box.height / 2
      const px = e.clientX - centerX
      const py = e.clientY - centerY

      const next = Math.max(1, Math.min(3, zoom + e.deltaY * -0.001))
      updateZoom(next, { x: px, y: py })
    },
    [zoom, updateZoom],
  )

  const lastTouchDistance = useRef<number | null>(null)

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      const d = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY)
      lastTouchDistance.current = d
    } else {
      handleMouseDown(e)
    }
  }

  const handleTouchMoveInner = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      if (e.touches.length === 2) {
        e.preventDefault()
        const d = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY)

        const box = cropperRef.current?.getBoundingClientRect()
        if (!box) return

        const cx = (e.touches[0].clientX + e.touches[1].clientX) / 2
        const cy = (e.touches[0].clientY + e.touches[1].clientY) / 2
        const centerX = box.left + box.width / 2
        const centerY = box.top + box.height / 2
        const px = cx - centerX
        const py = cy - centerY

        const prevD = lastTouchDistance.current ?? d
        lastTouchDistance.current = d

        const delta = (d - prevD) * 0.01
        const next = Math.max(1, Math.min(3, zoom + delta))
        updateZoom(next, { x: px, y: py })
      } else {
        handleMouseMove(e.nativeEvent)
      }
    },
    [zoom, updateZoom, handleMouseMove],
  )

  const handleTouchMove = useRafThrottle(handleTouchMoveInner)

  useEffect(() => {
    const cropperElement = cropperRef.current
    if (!cropperElement) return

    cropperElement.addEventListener('wheel', handleWheel, { passive: false })

    return () => {
      cropperElement.removeEventListener('wheel', handleWheel)
    }
  }, [handleWheel, cropperRef.current])

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
    window.addEventListener('touchmove', handleMouseMove, { passive: false })
    window.addEventListener('touchend', handleMouseUp)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
      window.removeEventListener('touchmove', handleMouseMove as any)
      window.removeEventListener('touchend', handleMouseUp)
    }
  }, [handleMouseMove])

  function useRafThrottle<T extends (...args: any[]) => void>(fn: T) {
    const frame = useRef<number | null>(null)
    const lastArgs = useRef<any[] | null>(null)
    const cb = useRef(fn)
    useEffect(() => {
      cb.current = fn
    }, [fn])

    return (...args: Parameters<T>) => {
      lastArgs.current = args
      if (frame.current != null) return
      frame.current = requestAnimationFrame(() => {
        frame.current = null
        // @ts-ignore
        cb.current(...(lastArgs.current || []))
      })
    }
  }
  const isIOS =
    typeof window !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream

  return (
    <EmtModal
      open={open}
      onClose={onClose}
      sx={{
        '& .children-box': { padding: 0, overflow: 'hidden', borderRadius: '24px', height: '100%' },
        '& .inner-box': {
          width: isMobile ? '100%' : isLargeTablet ? '90%' : '80%',
          maxWidth: 600,
          maxHeight: isMobile ? '100%' : '100vh',
        },
        zIndex: 1301,
      }}
    >
      <Box
        sx={{
          padding: isMobile ? '30px' : '40px',
          paddingBottom: '20px',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '20px',
          height: '100%',
        }}
      >
        <Box
          ref={cropperRef}
          key={resetKey}
          sx={{
            position: 'relative',
            width: '100%',
            aspectRatio: '1',
            overflow: 'hidden',
            cursor: 'move',
            backgroundColor: 'grey.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            touchAction: 'none',
            '& img': {
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: `translate(-50%, -50%) translate3d(${crop.x}px, ${crop.y}px, 0) rotate(${rotation}deg) scale(${zoom})`,
              transformOrigin: 'center',
              transition: 'none',
              userSelect: 'none',
              willChange: 'transform',
              maxWidth: 'none',
              maxHeight: 'none',
              objectFit: 'fill',
              width: aspectRatio > 1 ? 'auto' : '100%',
              height: aspectRatio > 1 ? '100%' : 'auto',
            },
          }}
          onMouseDown={!isMobile ? handleMouseDown : undefined}
          onTouchStart={isMobile ? handleTouchStart : undefined}
          onTouchMove={isMobile ? handleTouchMove : undefined}
        >
          <Box
            sx={{
              position: 'relative',
              width: `${cropSize}px`,
              height: `${cropSize}px`,
              backgroundColor: '#eff',
            }}
          >
            {normalizedSrc ? (
              <img
                ref={imgRef}
                src={normalizedSrc}
                alt="Crop preview"
                draggable={false}
                decoding="async"
                loading="eager"
                onLoad={() => {
                  setImgReady(true)
                  requestAnimationFrame(() => calculateCropArea())
                }}
              />
            ) : (
              <Box
                sx={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  transform: `translate(-50%, -50%)`,
                }}
              >
                <EmtLoader />
              </Box>
            )}
          </Box>
          <Box
            sx={{
              position: 'absolute',
              width: `${cropSize}px`,
              height: `${cropSize}px`,
              border: '1px solid white',
              boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
              pointerEvents: 'none',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          />
        </Box>

        {/* Controls */}
        <Box
          sx={{
            display: 'flex',
            gap: '30px',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            position: 'relative',
            pointerEvents: !normalizedSrc || !area ? 'none' : 'auto',
          }}
        >
          {/* 90° Rotation */}
          <Box
            sx={{
              display: 'flex',
              gap: '10px',
              justifyContent: 'center',
              alignItems: 'center',
              position: 'relative',
            }}
          >
            <RotateLeftIcon style={{ cursor: 'pointer' }} color="primary" onClick={rotateLeft} />
            <RotateRightIcon style={{ cursor: 'pointer' }} color="primary" onClick={rotateRight} />
          </Box>

          {/* Zoom */}
          <Box
            sx={{
              width: '100%',
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <EmtDistance
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(v: number | number[], _event: Event) => {
                // _event is the event object, value is the slider value
                if (isIOS && _event?.type === 'mousedown') {
                  return
                }
                const next = Array.isArray(v) ? v[0] : v
                updateZoom(next)
              }}
              valueLabelDisplay="auto"
              valueLabelFormat={(v) => `${v.toFixed(2)}x`}
              sx={{ margin: 0, width: '100%' }}
            />
          </Box>

          {/* Actions */}
          <EmtButton
            color={isPetsitterInRegistration || isPetSitter ? 'purple' : 'primary'}
            onClick={handleUse}
            loader={busy}
            disabled={!normalizedSrc || !imgReady || !area || area.width <= 1 || area.height <= 1}
            startIcon={<EmtIcon className="emt-tick" />}
          >
            {t('validate')}
          </EmtButton>
        </Box>
      </Box>
      {/* </Box> */}
    </EmtModal>
  )
}
